export const PAYMENT_METHOD_ONLINE = 'Online'
export const PAYMENT_METHOD_PARTIAL = 'Partial Payment'
export const PAYMENT_METHOD_ARRIVAL = 'Pay on Arrival'

export const PARTIAL_PAYMENT_PERCENT = 0.2
export const PARTIAL_PAYMENT_SERVICE_FEE = 50
export const BOOKING_SERVICE_FEE = PARTIAL_PAYMENT_SERVICE_FEE

export type PaymentBreakdown = {
  roomTotal: number
  isPartial: boolean
  partialDeposit: number
  remainingRoomBalance: number
  serviceFee: number
  serviceFeeDiscount: number
  serviceFeeCharged: number
  payNow: number
  dueAtCheckin: number
  totalCustomerPays: number
}

export function isPartialPayment(method: string | undefined | null): boolean {
  return (method || '').toLowerCase().includes('partial')
}

export function isOfflinePayment(method: string | undefined | null): boolean {
  const m = (method || '').toLowerCase()
  return m.includes('pay on arrival') || m.includes('cod')
}

export function requiresOnlineCheckout(method: string | undefined | null): boolean {
  if (!method) return false
  return !isOfflinePayment(method)
}

const SUCCESSFUL_PAYMENT_STATUSES = new Set([
  'confirmed',
  'success',
  'successful',
  'succeeded',
  'paid',
  'completed',
  'partial_paid',
  'ok',
])

const EXPLICIT_PAYMENT_FAILURE_STATUSES = new Set([
  'failed',
  'failure',
  'cancelled',
  'canceled',
  'user_dropped',
  'pending',
])

export function isSuccessfulPaymentStatus(status: string | undefined | null): boolean {
  return SUCCESSFUL_PAYMENT_STATUSES.has((status || '').trim().toLowerCase())
}

export function isExplicitPaymentFailure(status: string | undefined | null): boolean {
  return EXPLICIT_PAYMENT_FAILURE_STATUSES.has((status || '').trim().toLowerCase())
}

export function getPaymentFailureMessage(status?: string | null): string {
  const normalized = (status || '').trim().toLowerCase()
  if (!normalized) {
    return 'Payment was not completed. Please try again to confirm your booking.'
  }
  if (normalized === 'pending') {
    return 'Payment is still pending. Please complete payment to confirm your booking.'
  }
  if (normalized === 'cancelled' || normalized === 'canceled' || normalized === 'user_dropped') {
    return 'Payment was cancelled. Please try again.'
  }
  return 'Payment failed. Please try again or choose another payment method.'
}

export function calculatePaymentBreakdown(
  roomTotal: number,
  paymentMethod: string | undefined | null
): PaymentBreakdown {
  const total = Math.max(0, roomTotal)
  const serviceFee = BOOKING_SERVICE_FEE

  if (!isPartialPayment(paymentMethod)) {
    return {
      roomTotal: total,
      isPartial: false,
      partialDeposit: 0,
      remainingRoomBalance: 0,
      serviceFee,
      serviceFeeDiscount: serviceFee,
      serviceFeeCharged: 0,
      payNow: total,
      dueAtCheckin: 0,
      totalCustomerPays: total,
    }
  }

  const partialDeposit = Math.round(total * PARTIAL_PAYMENT_PERCENT * 100) / 100
  const remainingRoomBalance = Math.round((total - partialDeposit) * 100) / 100
  const payNow = Math.round((partialDeposit + serviceFee) * 100) / 100
  const dueAtCheckin = remainingRoomBalance

  return {
    roomTotal: total,
    isPartial: true,
    partialDeposit,
    remainingRoomBalance,
    serviceFee,
    serviceFeeDiscount: 0,
    serviceFeeCharged: serviceFee,
    payNow,
    dueAtCheckin,
    totalCustomerPays: Math.round((total + serviceFee) * 100) / 100,
  }
}

export function formatRupee(amount: number): string {
  return `₹${amount.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`
}

export function formatPartialPaymentLabel(roomTotal: number): string {
  const breakdown = calculatePaymentBreakdown(roomTotal, PAYMENT_METHOD_PARTIAL)
  return `Advance ${formatRupee(breakdown.partialDeposit)} + Service ${formatRupee(breakdown.serviceFee)}`
}

/** Dev-friendly log for booking payment amounts (browser console). */
export function logPaymentAmounts(
  paymentMethod: string,
  roomTotal: number,
  context = 'Booking'
): PaymentBreakdown {
  const breakdown = calculatePaymentBreakdown(roomTotal, paymentMethod)
  console.log(`[${context}] payment amounts`, {
    paymentMethod,
    roomTotal: breakdown.roomTotal,
    partialAmount: breakdown.isPartial ? breakdown.payNow : null,
    partialDeposit: breakdown.isPartial ? breakdown.partialDeposit : null,
    serviceFee: breakdown.isPartial ? breakdown.serviceFee : null,
    dueAtCheckin: breakdown.isPartial ? breakdown.dueAtCheckin : null,
    payNow: breakdown.payNow,
    totalCustomerPays: breakdown.totalCustomerPays,
  })
  return breakdown
}
